import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, DestroyRef, OnDestroy, OnInit } from '@angular/core';
import { PreloaderColor } from '@enums/shared.enum';
import { features } from '@consts/text.const';
import * as MenuActions from '@states/menu/menu.actions';
import { MenuKey } from '@enums/menu.enum';
import { select, Store } from '@ngrx/store';
import { SharedActions } from '@states/shared/shared.action-types';
import { TranslatePipe } from '@ngx-translate/core';
import { syncQueryParam } from '../../../../helpers/sync-query-param.helper';
import { UiPreloaderComponent } from '../../../../shared/ui-kit/ui-preloader/ui-preloader.component';
import { CustomObjectsSelectors } from '@states/custom-objects/custom-objects.selector-types';
import { CustomObjectStatesSelectors } from '@states/custom-objects-states/custom-objects-states.selector-types';
import { CustomObjectsActions } from '@states/custom-objects/custom-objects.action-types';
import { CustomObjectStatesActions } from '@states/custom-objects-states/custom-objects-states.action-types';
import { CustomObjectModels } from '@models/custom-object.model';
import { Actions, ofType } from '@ngrx/effects';
import { UiCustomObjectTrainingDialogService } from '../../../../shared/ui-kit/ui-custom-object-training-dialog/ui-custom-object-training-dialog.service';
import { UiCustomObjectStateCardComponent } from '../../../../shared/ui-kit/ui-custom-object-state-card/ui-custom-object-state-card.component';
import { MatDialog } from '@angular/material/dialog';
import {
  CustomObjectEditNameDialogData,
  CustomObjectEditNameDialogResult,
  UiCustomObjectEditNameDialogComponent,
  UiNoDataComponent,
} from '@ui';
import { ConfirmDialogService } from '../../../../shared/confirm-dialog/confirm-dialog.service';
import { ConfirmDialogModel } from '@ui';
import { Observable } from 'rxjs';
import { OrganizationSelectors } from '@states/organization/organization.selector-types';
import { AsyncPipe, NgIf } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type ViewState = 'LOADING' | 'HAS_DATA' | 'NO_RESULTS' | 'INITIAL_EMPTY';
type CustomObjectListItem = {
  key: string;
  item: CustomObjectModels.CustomObject | CustomObjectModels.StateCustomObject;
};

@Component({
  selector: 'app-custom-objects',
  templateUrl: './custom-objects.component.html',
  styleUrls: ['./custom-objects.component.scss'], imports: [TranslatePipe, UiNoDataComponent, UiPreloaderComponent, UiCustomObjectStateCardComponent, AsyncPipe, NgIf],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomObjectsComponent implements OnInit, OnDestroy {
  public preloaderColor = PreloaderColor;

  protected readonly query = syncQueryParam<string>('search');

  // Store selectors
  private selectSavedCustomObjects = this.store$.selectSignal(CustomObjectsSelectors.selectSavedCustomObjects);
  private selectSavedCustomObjectStates = this.store$.selectSignal(CustomObjectStatesSelectors.selectAllCustomObjectStates);
  private isLoading = this.store$.selectSignal(CustomObjectsSelectors.selectIsLoading);
  private isStatesLoading = this.store$.selectSignal(CustomObjectStatesSelectors.selectIsCustomObjectStatesLoading);

  protected filteredCustomObjects = computed(() => {
    const query = this.query();
    const customObjects = this.selectSavedCustomObjects();

    if (!query) {
      return customObjects;
    }

    return customObjects.filter((item) => item.name?.toLowerCase().includes(query.toLowerCase()));
  });

  protected filteredCustomObjectStates = computed(() => {
    const query = this.query();
    const customObjectStates = this.selectSavedCustomObjectStates();

    if (!query) {
      return customObjectStates;
    }

    const lowerQuery = query.toLowerCase();
    return customObjectStates.filter((item) =>
      item.states?.some((state) => state.toLowerCase().includes(lowerQuery)) ||
      item.cameraId?.toLowerCase().includes(lowerQuery),
    );
  });

  protected filteredCombinedCustomObjects = computed<CustomObjectListItem[]>(() => {
    const customObjects = this.filteredCustomObjects();
    const customObjectStates = this.filteredCustomObjectStates();

    const objectItems = customObjects.map((item) => ({
      key: `object-${item.customObjectId}`,
      item,
    }));
    const stateItems = customObjectStates.map((item) => ({
      key: `state-${item.id}`,
      item,
    }));

    return [...objectItems, ...stateItems];
  });

  protected readonly noResults = features.customObjects.noData;
  protected readonly noData = features.customObjects.noData;

  public readonly vm$ = computed(() => {
    const isLoading = this.isLoading() || this.isStatesLoading();
    const customObjects = this.selectSavedCustomObjects();
    const customObjectStates = this.selectSavedCustomObjectStates();
    const filteredCustomObjects = this.filteredCustomObjects();
    const filteredCustomObjectStates = this.filteredCustomObjectStates();
    const filteredCombinedCustomObjects = this.filteredCombinedCustomObjects();
    const query = this.query();
    const totalCount = customObjects.length + customObjectStates.length;
    const totalFiltered = filteredCustomObjects.length + filteredCustomObjectStates.length;

    let viewState: ViewState;
    if (isLoading && !totalCount) {
      viewState = 'LOADING';
    } else if (totalCount > 0 && query && !totalFiltered) {
      viewState = 'NO_RESULTS';
    } else if (totalCount > 0) {
      viewState = 'HAS_DATA';
    } else {
      viewState = 'INITIAL_EMPTY';
    }

    return {
      viewState,
      filteredCustomObjects: filteredCombinedCustomObjects,
      query,
    };
  });

  public selectCustomObjectsEnabled$: Observable<boolean> = this.store$.pipe(
    select(OrganizationSelectors.selectCustomObjectsEnabled),
  );

  constructor(
    private store$: Store,
    private actions$: Actions,
    private trainingDialogService: UiCustomObjectTrainingDialogService,
    private dialog: MatDialog, private cd: ChangeDetectorRef,
    private confirm: ConfirmDialogService,
    private destroyRef: DestroyRef,
  ) {}

  public ngOnInit(): void {
    this.store$.dispatch(
      MenuActions.setActiveLevel2Menu({
        activeLevel2: MenuKey.customObjects,
      }),
    );
    this.store$.dispatch(SharedActions.setIsLoading({ isLoading: false }));

    // Load saved custom objects
    // this.store$.dispatch(CustomObjectsActions.getSavedCustomObjects());
    this.store$.dispatch(CustomObjectsActions.getSavedCustomObjectStates());

    // Listen for successful delete operations to refresh the list
    this.actions$.pipe(
      ofType(CustomObjectsActions.deleteCustomObjectSuccess),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      // Refresh the saved custom objects list after successful deletion
      this.store$.dispatch(CustomObjectsActions.getSavedCustomObjectStates());
    });

    // Listen for successful state custom object delete to refresh the list
    this.actions$.pipe(
      ofType(CustomObjectStatesActions.deleteCustomObjectStateSuccess),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      // The reducer already removes the item from the store, but refresh to ensure consistency
      this.cd.markForCheck();
    });

    // Listen for successful state custom object name update
    this.actions$.pipe(
      ofType(CustomObjectStatesActions.updateCustomObjectStateNameSuccess),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.cd.markForCheck();
    });
  }

  public ngOnDestroy(): void {}

  public onCreateCustomObject(): void {
    this.trainingDialogService.openTrainingDialog()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result && result.saved) {
          // Refresh the custom objects list
          this.store$.dispatch(CustomObjectsActions.getSavedCustomObjectStates());
        }
      });
  }

  public onEditCustomObjectName(customObject: CustomObjectModels.CustomObject | CustomObjectModels.StateCustomObject): void {
    if (this.isStandardCustomObject(customObject)) {
      // Standard custom object
      const dialogRef = this.dialog.open<
        UiCustomObjectEditNameDialogComponent,
        CustomObjectEditNameDialogData,
        CustomObjectEditNameDialogResult
      >(UiCustomObjectEditNameDialogComponent, {
        data: {
          currentName: customObject.name,
          customObjectId: customObject.customObjectId!,
        },
        width: '500px',
      });

      dialogRef
        .afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((result) => {
          if (result?.saved && result.newName && customObject.customObjectId) {
            this.store$.dispatch(
              CustomObjectsActions.updateCustomObjectName({
                customObjectId: customObject.customObjectId,
                name: result.newName,
              }),
            );
          }
        });
    } else if (this.isStateCustomObject(customObject)) {
      // State custom object
      const dialogRef = this.dialog.open<
        UiCustomObjectEditNameDialogComponent,
        CustomObjectEditNameDialogData,
        CustomObjectEditNameDialogResult
      >(UiCustomObjectEditNameDialogComponent, {
        data: {
          currentName: customObject.name || '',
          customObjectId: customObject.id,
        },
        width: '500px',
      });

      dialogRef
        .afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((result) => {
          if (result?.saved && result.newName && customObject.id) {
            this.store$.dispatch(
              CustomObjectStatesActions.updateCustomObjectStateName({
                stateId: customObject.id,
                name: result.newName,
              }),
            );
          }
        });
    }
  }

  public onDeleteCustomObject(customObject: CustomObjectModels.CustomObject | CustomObjectModels.StateCustomObject): void {
    const displayName = this.isStateCustomObject(customObject)
      ? (customObject.name || customObject.states?.join('/') || 'Custom Object')
      : customObject.name;

    this.confirm.open({
      title: 'Delete Custom Object',
      msg: `Are you sure you want to delete "${displayName}"?`,
      confirm: 'Delete',
      cancel: 'Cancel',
      type: ConfirmDialogModel.ConfirmDialogType.CONFIRM
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((confirmed) => {
      if (!!confirmed.selection) {
        if (this.isStandardCustomObject(customObject) && customObject.customObjectId) {
          this.store$.dispatch(CustomObjectsActions.deleteCustomObject({
            customObjectId: customObject.customObjectId
          }));
        } else if (this.isStateCustomObject(customObject) && customObject.id) {
          this.store$.dispatch(CustomObjectStatesActions.deleteCustomObjectState({
            stateId: customObject.id
          }));
        }
      }
    });
  }

  public onRetrainCustomObject(customObject: CustomObjectModels.CustomObject | CustomObjectModels.StateCustomObject): void {
    if (!this.isStandardCustomObject(customObject)) {
      return;
    }
    // TODO: Implement retrain custom object in next stage
    console.log('Retrain custom object - to be implemented');
  }

  private isStandardCustomObject(
    customObject: CustomObjectModels.CustomObject | CustomObjectModels.StateCustomObject,
  ): customObject is CustomObjectModels.CustomObject {
    return (customObject as CustomObjectModels.CustomObject)?.customObjectId !== undefined;
  }

  private isStateCustomObject(
    customObject: CustomObjectModels.CustomObject | CustomObjectModels.StateCustomObject,
  ): customObject is CustomObjectModels.StateCustomObject {
    return (customObject as CustomObjectModels.StateCustomObject)?.states !== undefined;
  }
}
